/**
 * 作り直しの変更の読取（HIST §3.3・API §4.0）— 何を、どちらの表から読むか。
 *
 * 今は 1 枚の変更ログを全部の問いに当てている。作り直しでは問いごとに読む表が分かれる。
 *
 * | 知りたいこと | 読む表 |
 * |---|---|
 * | その案件が何を変えるか | 案件の変更（案件 × ノードで 1 行、`status` 付き、結ばれた要求） |
 * | そのノードにいつ誰が何をしたか | 操作履歴（`kind` はノードへの操作の 4 値に絞る） |
 * | 案件の一覧 | 案件の変更を案件で畳んだ件数と、その案件が持つ要求の件数。案件名は Backlog の課題の取り込み表 |
 * | なぜ変えたか | 要求と why（結ぶ相手は（案件, ノード）） |
 *
 * **理由の欄は読まない。** 変更行の `reason` は無くなり、なぜ変えたかは要求と why の層が持つ
 * （決定 3）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの案件の変更・操作履歴・案件の一覧・案件の要求を BigQuery から読む
 * @graph-connects bigquery.spec_graph_pj_changes [reads_from, via:pjNumber] 案件の変更の行と案件の一覧の読取
 * @graph-connects bigquery.spec_graph_operations [reads_from, via:targetId] 操作履歴の読取
 * @graph-connects bigquery.spec_graph_requirement_changes [reads_from, via:pjNumber] 要求と（案件, ノード）の結びの読取
 * @graph-connects bigquery.spec_graph_pj_requirements [reads_from, via:pjNumber] 案件が持つ要求の読取
 * @graph-connects bigquery.spec_graph_requirements [reads_from, via:requirementId] 要求の本文の読取
 * @graph-connects bigquery.spec_graph_requirement_whys [reads_from, via:requirementId] 要求と why の結びの読取
 * @graph-connects bigquery.spec_graph_whys [reads_from, via:whyId] why の本文の読取
 * @graph-connects bigquery.spec_graph_why_evidences [reads_from, via:whyId] 根拠の読取
 * @graph-connects backlog_no_embedding.issues_raw [reads_from, via:issue_key] 案件の一覧の案件名（Backlog の課題の件名）
 */
import { bqIdentifier } from '@cortex/bigquery/schema';
import { SPEC_ELEMENT_TABLE_IDS, SPEC_NODE_OPERATION_KINDS, SPEC_OPERATION_KINDS, SPEC_PJ_CHANGE_STATUSES, } from '../../contract/elements/index.js';
import { buildPage, limitClause, resolvePage } from '../paging.js';
import { asRecord, columnString, runReadQuery, tableRef, } from '../read-client.js';
// 案件の差分（欄ごとの前後）は案件の変更の行から組む純関数で、束ね役を変えずに公開するためここから出す。
export * from './pj-diff.js';
/**
 * 操作履歴の「操作した日時」の列。**囲んだ形で 1 箇所に持つ。**
 *
 * `at` は GoogleSQL の 97 語の予約語の 1 つなので、裸で書いた参照はどれも
 * `Unexpected keyword AT` で落ちる。参照するたびに手で囲むと、囲み忘れた 1 箇所だけが
 * 本番で落ちるので、囲んだ綴りを定数にして全部の参照をここへ通す。
 * @graph-connects none
 */
const AT_COLUMN = bqIdentifier('at');
/**
 * 案件の一覧が返す状態 2 値。案件の行に 1 つでも `merged` があれば確定済みである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の状態 2 値の allowlist（未確定 / 確定済み）
 * @graph-connects none
 */
export const PJ_STATES = ['open', 'merged'];
/**
 * Backlog の課題キーの前置き。案件番号はこの後ろの数値部分である。
 *
 * **前置きの綴りはここに 1 つだけ置く。** 案件の一覧は件名を引く鍵に、コンソールの API は憲章を
 * 引く鍵に、画面は案件番号の表記に同じ前置きを使う。片方だけ直すと、画面の綴りと読む鍵が
 * 黙ってずれる（コンソールの契約 `@cortex/domain-spec-console` はここを再輸出する）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business Backlog の課題キーの前置き（案件番号の前に付く綴り）
 * @graph-connects none
 */
export const PJ_KEY_PREFIX = 'AIRCLOSET-';
/**
 * 案件番号から Backlog の課題キーを組む。0 で埋めない（課題キーも 0 で埋めない）。
 *
 * 仕様グラフの案件番号は課題キーの数値部分がそのまま入る（決定。2026-09-21 — `specPjNumberValue`）
 * ので、課題キーは番号から一意に組める。
 *
 * @param pjNumber 案件番号（1 以上の整数）
 * @returns 課題キー（`AIRCLOSET-<番号>`）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件番号を、前置きつきの Backlog の課題キーへ直す。
 * @graph-connects none
 */
export function pjKeyOf(pjNumber) {
    return `${PJ_KEY_PREFIX}${String(pjNumber)}`;
}
/**
 * 案件の件名を読む Backlog の課題の取り込み表。仕様グラフと同じプロジェクトの別データセットにある。
 *
 * 表の持ち主は backlog の取り込み（`infra/pipeline/backlog`）で、読む側の権限は表単位で付ける
 * （`infra/api/spec-console` と `infra/mcp/spec-graph`）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の件名を読む Backlog の課題の取り込み表（データセットと表）
 * @graph-connects none
 */
export const BACKLOG_ISSUES_TABLE = {
    dataset: 'backlog_no_embedding',
    table: 'issues_raw',
};
/** BigQuery が返した値を文字列にする。TIMESTAMP は `{ value }` のラッパで返る。 @graph-connects none */
function readString(record, key) {
    return columnString(record, key) ?? '';
}
/**
 * BigQuery が返した値を整数にする。INT64 は大きい値でラッパになる。
 *
 * 整数に読めない値は投げる。番号の列に整数でない値が入っているのは書込側の不具合で、0 へ
 * 畳むと別のノードを指す行が静かに生まれる。
 * @graph-connects none
 */
function readNumber(record, key) {
    const raw = record[key];
    const parsed = typeof raw === 'number' ? raw : Number(readString(record, key));
    if (!Number.isInteger(parsed)) {
        throw new Error(`spec-graph: ${key} に整数でない値が入っている: ${String(raw)}`);
    }
    return parsed;
}
/** BigQuery が返した値を、NULL を許す整数にする。 @graph-connects none */
function readNullableNumber(record, key) {
    const raw = record[key];
    return raw === null || raw === undefined ? null : readNumber(record, key);
}
/** BigQuery が返した値を、NULL を許す文字列にする。 @graph-connects none */
function readNullableString(record, key) {
    const raw = record[key];
    return raw === null || raw === undefined ? null : readString(record, key);
}
/** 行を読み、読めなかった行を落とす。 @graph-connects none */
function records(rows) {
    return rows.flatMap((row) => {
        const record = asRecord(row);
        return record === null ? [] : [record];
    });
}
/**
 * 案件の変更の行の状態を 5 値の閉集合と突き合わせる。
 *
 * 列の型は STRING なので、壊れた値は TS の型では捕まらない。素通しすると、画面が知らない
 * 状態の行を「未確定」として描く。
 * @graph-connects none
 */
function readStatus(record) {
    const raw = readString(record, 'status');
    const status = SPEC_PJ_CHANGE_STATUSES.find((value) => value === raw);
    if (status === undefined) {
        throw new Error(`spec-graph: 案件の変更の行に 5 値でない状態が入っている: ${raw}`);
    }
    return status;
}
/** 操作履歴の行の種類を 9 値の閉集合と突き合わせる。 @graph-connects none */
function readOperationRow(record) {
    const raw = readString(record, 'kind');
    const kind = SPEC_OPERATION_KINDS.find((value) => value === raw);
    if (kind === undefined) {
        throw new Error(`spec-graph: 操作履歴の行に 9 値でない種類が入っている: ${raw}`);
    }
    return {
        opId: readNumber(record, 'opId'),
        at: readString(record, 'at'),
        actor: readString(record, 'actor'),
        pjNumber: readNumber(record, 'pjNumber'),
        kind,
        targetId: readNullableNumber(record, 'targetId'),
        before: readNullableString(record, 'before'),
        after: readNullableString(record, 'after'),
        refOpId: readNullableNumber(record, 'refOpId'),
    };
}
/** 同じ鍵の値を配列に束ねる。並びは入力の順を保つ。 @graph-connects none */
function groupBy(rows, key) {
    const grouped = new Map();
    for (const row of rows) {
        const bucket = grouped.get(key(row));
        if (bucket === undefined)
            grouped.set(key(row), [row]);
        else
            bucket.push(row);
    }
    return grouped;
}
/**
 * その案件の変更の行を、結ばれた要求と一緒に読む。
 *
 * **案件番号の等値比較から入る。** 案件の変更は `pjNumber` で分割されるので、これだけで刈れる。
 * 今の実装が同じ問いに 517 行を畳んで答えていたのに対し、こちらは 38 行をそのまま返す。
 *
 * 並びはノードの id の順である。時刻で並べると、同じ案件を読み直すたびに並びが変わる。
 *
 * @param config 読取接続
 * @param pjNumber 案件番号
 * @returns その案件の変更の行（ノードの id の順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business その案件の変更の行を、結ばれた要求の id と一緒に読む
 * @graph-connects bigquery.spec_graph_pj_changes [reads_from, via:pjNumber] 案件の変更の行の読取
 * @graph-connects bigquery.spec_graph_requirement_changes [reads_from, via:pjNumber] 結ばれた要求の読取
 */
export async function listPjChanges(config, pjNumber) {
    const [changeRows, linkRows] = await Promise.all([
        runReadQuery(config, `SELECT pjNumber, nodeId, before, after, status, FORMAT_TIMESTAMP('%FT%H:%M:%E*SZ', updatedAt, 'UTC') AS updatedAt, actor
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.pjChanges)}
WHERE pjNumber = @pjNumber
ORDER BY nodeId`, { pjNumber }),
        runReadQuery(config, `SELECT nodeId, requirementId FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.requirementLinks)} WHERE pjNumber = @pjNumber`, { pjNumber }),
    ]);
    const requirementIdsByNode = groupBy(records(linkRows).map((record) => ({
        nodeId: readNumber(record, 'nodeId'),
        requirementId: readNumber(record, 'requirementId'),
    })), (link) => link.nodeId);
    return records(changeRows).map((record) => {
        const nodeId = readNumber(record, 'nodeId');
        return {
            pjNumber: readNumber(record, 'pjNumber'),
            nodeId,
            before: readNullableString(record, 'before'),
            after: readNullableString(record, 'after'),
            status: readStatus(record),
            updatedAt: readString(record, 'updatedAt'),
            actor: readString(record, 'actor'),
            requirementIds: [
                ...new Set((requirementIdsByNode.get(nodeId) ?? []).map((link) => link.requirementId)),
            ].sort((left, right) => left - right),
        };
    });
}
/**
 * 操作履歴を、ノードか期間で引く。
 *
 * **ノードで引くときは `kind` を 4 値に絞る。** `targetId` がノードの id なのはノードへの操作の
 * ときだけで（HIST §3.2）、絞らないと要求や why の id をノードの id として拾う。
 *
 * 絞り込みが 1 つも無い呼び出しは受け付けない。表の全件走査になり、履歴の入口として意味を
 * 成さない（今の `get_spec_changes` が同じ形式検査を持つ）。
 *
 * 並びは新しい順、同時刻は `opId` の順である。
 *
 * @param config 読取接続
 * @param input 絞り込み
 * @returns 操作履歴のページ
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 操作履歴をノードか期間で引き、ノードで引くときは種類を 4 値に絞る
 * @graph-connects bigquery.spec_graph_operations [reads_from, via:targetId] 操作履歴の読取
 */
export async function listOperations(config, input) {
    const from = input.period?.from ?? '';
    const to = input.period?.to ?? '';
    if (input.nodeId === undefined && from === '' && to === '') {
        throw new Error('spec-graph: 操作履歴はノードか期間のどちらかで絞る（全件走査になる）');
    }
    const range = resolvePage(input.page);
    const clauses = [
        ...(input.nodeId === undefined ? [] : ['targetId = @nodeId', 'kind IN UNNEST(@nodeKinds)']),
        // 列 `at` は GoogleSQL の予約語なので、参照するたびに囲む。囲まずに書いた WHERE / SELECT /
        // ORDER BY は BigQuery へ投げた瞬間に `Unexpected keyword AT` で落ちる（型検査もテストも
        // 通るため、組む側で囲む以外に防ぎ方が無い）。
        ...(from === '' ? [] : [`${AT_COLUMN} >= TIMESTAMP(@from)`]),
        ...(to === '' ? [] : [`${AT_COLUMN} <= TIMESTAMP(@to)`]),
    ];
    const params = {
        ...(input.nodeId === undefined
            ? {}
            : { nodeId: input.nodeId, nodeKinds: SPEC_NODE_OPERATION_KINDS }),
        ...(from === '' ? {} : { from }),
        ...(to === '' ? {} : { to }),
    };
    const rows = await runReadQuery(config, `SELECT opId, FORMAT_TIMESTAMP('%FT%H:%M:%E*SZ', ${AT_COLUMN}, 'UTC') AS ${AT_COLUMN}, actor, pjNumber, kind, targetId, before, after, refOpId, COUNT(*) OVER () AS totalCount
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.operations)}
WHERE ${clauses.join('\n  AND ')}
ORDER BY ${AT_COLUMN} DESC, opId DESC
${limitClause(range)}`, params);
    const read = records(rows);
    const totalCount = read.length === 0 ? 0 : readNumber(read[0] ?? {}, 'totalCount');
    return buildPage(read.map(readOperationRow), range, totalCount);
}
/**
 * 案件を一覧する。**期間は当てない。**
 *
 * 期間を当てると、それより前に触られた案件が一覧から静かに消える。消えるのは確定し忘れた
 * 案件から順であり、それがまさに見落としてはいけないものである（今の `listSpecPjs` と同じ判断）。
 *
 * 件数は 2 つだけ返す（決定 16）。変更の行数は案件の変更を案件で畳んで数え、要求の件数は
 * 案件が持つ要求の表から数える。
 *
 * 案件名は Backlog の課題の件名で、案件の一覧が揃ってから全件分を 1 本の問い合わせで引く
 * （`readPjNames`）。案件ごとに問い合わせない。コンソールの画面（`listPjs`）と MCP
 * （`list_spec` の `pjs`）はどちらもこの関数を通すので、2 つの入口で案件名が食い違わない。
 *
 * @param config 読取接続
 * @returns 案件の一覧（最終更新の新しい順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更を案件で畳み、案件名・状態・変更の行数・要求の件数つきで案件を一覧する
 * @graph-connects bigquery.spec_graph_pj_changes [reads_from, via:pjNumber] 案件の一覧の畳み込み
 * @graph-connects bigquery.spec_graph_pj_requirements [reads_from, via:pjNumber] 案件が持つ要求の件数
 * @graph-connects readPjNames [calls] 案件名（Backlog の課題の件名）をまとめて引く
 */
export async function listPjs(config) {
    const [pjRows, requirementRows] = await Promise.all([
        runReadQuery(config, `SELECT
  pjNumber,
  FORMAT_TIMESTAMP('%FT%H:%M:%E*SZ', MAX(updatedAt), 'UTC') AS updatedAt,
  COUNT(*) AS changeCount,
  LOGICAL_OR(status = @mergedStatus) AS merged
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.pjChanges)}
GROUP BY pjNumber
ORDER BY updatedAt DESC, pjNumber`, { mergedStatus: 'merged' }),
        runReadQuery(config, `SELECT pjNumber, COUNT(DISTINCT requirementId) AS requirementCount
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.pjRequirements)}
GROUP BY pjNumber`, {}),
    ]);
    const requirementCounts = new Map(records(requirementRows).map((record) => [
        readNumber(record, 'pjNumber'),
        readNumber(record, 'requirementCount'),
    ]));
    const pjs = records(pjRows).map((record) => ({
        pjNumber: readNumber(record, 'pjNumber'),
        merged: record['merged'] === true,
        updatedAt: readString(record, 'updatedAt'),
        changeCount: readNumber(record, 'changeCount'),
    }));
    const names = await readPjNames(config, pjs.map((pj) => pj.pjNumber));
    return pjs.map((pj) => ({
        pjNumber: pj.pjNumber,
        name: names.get(pjKeyOf(pj.pjNumber)) ?? null,
        state: pj.merged ? 'merged' : 'open',
        updatedAt: pj.updatedAt,
        changeCount: pj.changeCount,
        requirementCount: requirementCounts.get(pj.pjNumber) ?? 0,
    }));
}
/**
 * 案件の件名（Backlog の課題の件名）を、案件の課題キーでまとめて引く。**問い合わせは 1 本である。**
 *
 * 読み方は Product Innovation Lab のチケット一括照会（`apps/api/product` の
 * `buildTicketLookupQuery`）と同じにする（決定 U21）。取り込みの表は同じ課題を取るたびに行を
 * 積むので、課題ごとに最新の行へ絞ってから `$.summary` を読む。課題キーの絞り込みを窓関数より
 * 前に置くのは、読む行を案件の課題だけにするためである（同じ課題の行は同じ課題キーを持つので、
 * 最新の行の選び方は変わらない）。
 *
 * 件名が取れなかった課題は結果に入らない。呼び出し側は「件名なし」として扱う。
 *
 * @param config 読取接続（課題の表は同じプロジェクトの別データセットにある）
 * @param pjNumbers 案件番号
 * @returns 課題キー → 件名
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件番号を Backlog の課題キーへ直し、課題の取り込み表の最新の行から件名を 1 本の問い合わせで引く
 * @graph-connects backlog_no_embedding.issues_raw [reads_from, via:issue_key] 案件の件名（Backlog の課題の件名）
 */
async function readPjNames(config, pjNumbers) {
    // 空の配列はパラメータの型が決まらないので、文ごと投げない（`runReadQuery` の約束）。
    if (pjNumbers.length === 0)
        return new Map();
    const rows = await runReadQuery(config, `SELECT issue_key, JSON_VALUE(raw_json, "$.summary") AS summary
FROM \`${config.project}.${BACKLOG_ISSUES_TABLE.dataset}.${BACKLOG_ISSUES_TABLE.table}\`
WHERE issue_key IN UNNEST(@issueKeys)
QUALIFY ROW_NUMBER() OVER (PARTITION BY id ORDER BY fetched_at DESC) = 1`, { issueKeys: pjNumbers.map(pjKeyOf) });
    const names = new Map();
    for (const record of records(rows)) {
        const summary = columnString(record, 'summary');
        if (summary !== null && summary !== '')
            names.set(readString(record, 'issue_key'), summary);
    }
    return names;
}
/**
 * 案件の要求を、why・根拠・結ばれた（案件, ノード）と一緒に読む。
 *
 * **結ぶ相手が変更行から（案件, ノード）へ替わる**（HIST §3.3）。要求は案件を跨げるので、
 * 結びは案件で絞って読む — 読んでいる案件の外のノードを混ぜると、画面がその案件に無い行を
 * 指しに行く。
 *
 * 要求は立てた時刻の古い順に並ぶ。案件で最初に立てた要求が上に来るので、並びが「どの順で
 * 分解したか」と一致する。
 *
 * @param config 読取接続
 * @param pjNumber 案件番号
 * @returns その案件が持つ要求（立てた時刻の古い順）
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の要求を why・根拠・結ばれた案件の変更のノードと一緒に読む
 * @graph-connects bigquery.spec_graph_pj_requirements [reads_from, via:pjNumber] 案件が持つ要求の id
 * @graph-connects bigquery.spec_graph_requirements [reads_from, via:requirementId] 要求の本文
 * @graph-connects bigquery.spec_graph_requirement_whys [reads_from, via:requirementId] 要求と why の結び
 * @graph-connects bigquery.spec_graph_whys [reads_from, via:whyId] why の本文
 * @graph-connects bigquery.spec_graph_why_evidences [reads_from, via:whyId] 根拠
 * @graph-connects bigquery.spec_graph_requirement_changes [reads_from, via:pjNumber] 要求と案件の変更の結び
 */
export async function listPjRequirements(config, pjNumber) {
    const t = (tableId) => tableRef(config, tableId);
    // ① 案件が持つ要求の id。クラスタ先頭列（pjNumber）の等値で刈れる。
    const requirementIds = [
        ...new Set(records(await runReadQuery(config, `SELECT requirementId FROM ${t(SPEC_ELEMENT_TABLE_IDS.pjRequirements)} WHERE pjNumber = @pjNumber`, { pjNumber })).map((record) => readNumber(record, 'requirementId'))),
    ];
    // 空の配列パラメータは型が決まらないので、後段の文を組まずに返す。
    if (requirementIds.length === 0)
        return [];
    // ② 要求の本文と 2 本の結び。
    const [requirementRows, whyLinkRows, nodeLinkRows] = await Promise.all([
        runReadQuery(config, `SELECT requirementId, statement, acceptance, FORMAT_TIMESTAMP('%FT%H:%M:%E*SZ', raisedAt, 'UTC') AS raisedAt
FROM ${t(SPEC_ELEMENT_TABLE_IDS.requirements)}
WHERE requirementId IN UNNEST(@requirementIds)`, { requirementIds }),
        runReadQuery(config, `SELECT requirementId, whyId FROM ${t(SPEC_ELEMENT_TABLE_IDS.requirementWhys)} WHERE requirementId IN UNNEST(@requirementIds)`, { requirementIds }),
        runReadQuery(config, `SELECT requirementId, nodeId FROM ${t(SPEC_ELEMENT_TABLE_IDS.requirementLinks)} WHERE pjNumber = @pjNumber AND requirementId IN UNNEST(@requirementIds)`, { pjNumber, requirementIds }),
    ]);
    const whyLinks = records(whyLinkRows).map((record) => ({
        requirementId: readNumber(record, 'requirementId'),
        whyId: readNumber(record, 'whyId'),
    }));
    const whyIds = [...new Set(whyLinks.map((link) => link.whyId))];
    // ③ why と根拠。
    const [whyRows, evidenceRows] = whyIds.length === 0
        ? [[], []]
        : await Promise.all([
            runReadQuery(config, `SELECT whyId, statement, outcome, actor FROM ${t(SPEC_ELEMENT_TABLE_IDS.whys)} WHERE whyId IN UNNEST(@whyIds)`, { whyIds }),
            runReadQuery(config, `SELECT whyId, source, FORMAT_TIMESTAMP('%FT%H:%M:%E*SZ', observedAt, 'UTC') AS observedAt, value
FROM ${t(SPEC_ELEMENT_TABLE_IDS.whyEvidences)}
WHERE whyId IN UNNEST(@whyIds)
ORDER BY whyId, observedAt`, { whyIds }),
        ]);
    const evidencesByWhy = groupBy(records(evidenceRows).map((record) => ({
        whyId: readNumber(record, 'whyId'),
        source: readString(record, 'source'),
        observedAt: readString(record, 'observedAt'),
        value: readString(record, 'value'),
    })), (evidence) => evidence.whyId);
    const whyById = new Map(records(whyRows).map((record) => {
        const whyId = readNumber(record, 'whyId');
        return [
            whyId,
            {
                whyId,
                statement: readString(record, 'statement'),
                outcome: readString(record, 'outcome'),
                actor: readString(record, 'actor'),
                evidences: (evidencesByWhy.get(whyId) ?? []).map((evidence) => ({
                    source: evidence.source,
                    observedAt: evidence.observedAt,
                    value: evidence.value,
                })),
            },
        ];
    }));
    const whyIdsByRequirement = groupBy(whyLinks, (link) => link.requirementId);
    const nodeIdsByRequirement = groupBy(records(nodeLinkRows).map((record) => ({
        requirementId: readNumber(record, 'requirementId'),
        nodeId: readNumber(record, 'nodeId'),
    })), (link) => link.requirementId);
    return records(requirementRows)
        .map((record) => {
        const requirementId = readNumber(record, 'requirementId');
        return {
            requirementId,
            statement: readString(record, 'statement'),
            acceptance: readString(record, 'acceptance'),
            raisedAt: readString(record, 'raisedAt'),
            whys: (whyIdsByRequirement.get(requirementId) ?? []).flatMap((link) => {
                const why = whyById.get(link.whyId);
                return why === undefined ? [] : [why];
            }),
            nodeIds: [
                ...new Set((nodeIdsByRequirement.get(requirementId) ?? []).map((link) => link.nodeId)),
            ].sort((left, right) => left - right),
        };
    })
        .sort((left, right) => left.raisedAt.localeCompare(right.raisedAt) || left.requirementId - right.requirementId);
}
//# sourceMappingURL=pj-read.js.map